import Analytics from '@/utils/analytics';
import ChevronDownIcon from '@assets/icons/chevron-down-rounded.svg';
import i18n from '@/utils/i18n';
import LockIcon from '@assets/icons/lock.svg';
import PlusIcon from '@assets/icons/plus.svg';
import React from 'react';
import { Cart, useCartActions } from './hooks/use-cart-actions';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { Pressable, ScrollView, Text } from 'react-native';

interface CartSwitcherTabsProps {
  onNewCartPress: () => void;
  onSeeAllPress: () => void;
}

export const CartSwitcherTabs: React.FC<CartSwitcherTabsProps> = ({
  onNewCartPress,
  onSeeAllPress,
}) => {
  const [styles, theme] = useStyles();
  const {
    activeCart,
    atCap,
    carts,
    hasOverflow,
    selectCart,
    showCartLimitToast,
    visibleCarts,
  } = useCartActions();

  return (
    <ScrollView
      contentContainerStyle={styles.row}
      horizontal
      showsHorizontalScrollIndicator={false}
      style={styles.container}
    >
      {visibleCarts.map((cart) => (
        <CartTab
          active={cart.id === activeCart.id}
          cart={cart}
          key={cart.id}
          onPress={() => selectCart(cart)}
        />
      ))}
      {hasOverflow ? (
        <Pressable
          onPress={() => {
            Analytics.track('multi_cart.all_carts.open', {
              cartCount: carts.length,
            });
            onSeeAllPress();
          }}
          style={({ pressed }) => [
            styles.seeAllButton,
            pressed ? styles.pressed : null,
          ]}
          testID="see-all-carts-button"
        >
          <Text style={[TYPOGRAPHY.h4SemiBold, styles.seeAllLabel]}>
            {i18n.t('see_all_count', { count: carts.length })}
          </Text>
          <ChevronDownIcon
            height={12}
            stroke={theme.sk.color.text.tertiary}
            width={12}
          />
        </Pressable>
      ) : null}
      <Pressable
        onPress={() => {
          if (atCap) {
            showCartLimitToast();
            return;
          }
          Analytics.track('multi_cart.cart_form.open', { isEdit: false });
          onNewCartPress();
        }}
        style={({ pressed }) => [
          styles.newButton,
          atCap ? styles.newButtonDisabled : null,
          pressed ? styles.pressed : null,
        ]}
        testID="new-cart-button"
      >
        <PlusIcon
          height={14}
          stroke={theme.sk.color.text.tertiary}
          width={14}
        />
      </Pressable>
    </ScrollView>
  );
};

const CartTab: React.FC<{
  active: boolean;
  cart: Cart;
  onPress: () => void;
}> = ({ cart, active, onPress }) => {
  const [styles, theme] = useStyles();
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.tab,
        active ? styles.tabActive : styles.tabInactive,
        pressed ? styles.pressed : null,
      ]}
      testID={`cart-tab-${cart.id}`}
    >
      <Text
        numberOfLines={1}
        style={[TYPOGRAPHY.h4SemiBold, active ? styles.tabLabelActive : null]}
      >
        {cart.name}
      </Text>
      {cart.visibility === 'PRIVATE' ? (
        <LockIcon
          height={15}
          stroke={
            active ? theme.sk.color.icon.ondark : theme.sk.color.icon.tertiary
          }
          width={12}
        />
      ) : null}
    </Pressable>
  );
};

const useStyles = createStyles((theme) => ({
  container: { flexGrow: 0, paddingBottom: 12, paddingTop: 13 },
  newButton: {
    alignItems: 'center',
    borderColor: theme.sk.color.border.secondary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderStyle: 'dashed',
    borderWidth: 1,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  newButtonDisabled: { opacity: 0.4 },
  pressed: { opacity: 0.5 },
  row: {
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
  },
  seeAllButton: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.button.secondary.default,
    borderColor: theme.sk.color.border.secondary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    flexDirection: 'row',
    gap: 6,
    height: 40,
    paddingHorizontal: 12,
  },
  seeAllLabel: {
    ...FONTS.primaryBold,
    color: theme.sk.color.text.primary,
  },
  tab: {
    alignItems: 'center',
    borderRadius: theme.sk.radii.radiusMedium,
    flexDirection: 'row',
    gap: 6,
    height: 40,
    paddingHorizontal: 16,
  },
  tabActive: { backgroundColor: theme.sk.color.bg.button.primary.default },
  tabInactive: {
    backgroundColor: theme.sk.color.bg.button.secondary.default,
    borderColor: theme.sk.color.border.secondary,
    borderWidth: 1,
  },
  tabLabelActive: { ...FONTS.primaryBold, color: theme.colors.white },
}));
